import { useSyncExternalStore } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  changeSetCommentRowFromGridKey,
  type As400ExportGroup,
} from '@erp/rpc';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { toast } from '@/components/ui/toast';
import { NoteComposer } from '@/components/common/comments/NoteComposer';
import {
  useChangeSetMentionableUsers,
  useCreateChangeSetCommentThread,
} from '@/api/rpc/hooks/inventory-management/useChangeSetComments';
import { worksheetColumnHeader } from '../as400GridSort';
import type { ValueStore } from '../valueStore';
import { threadsForCell, type CellThreadIndex } from './cellComments';
import { ReviewCommentThread } from './ReviewCommentThread';

/** The grid cell whose comments are open, as the grid showed it then. */
export interface OpenCommentCell {
  /** The grid row's key (`As400ExportRow.key`, or `new:<id>`). */
  rowKey: string;
  columnKey: string;
  /** The row's item number and CDB as shown — a new thread's labels. */
  itemNumber: string | null;
  cdb: string | null;
  /** The cell's viewport box when it was opened. */
  rect: { left: number; top: number; width: number; height: number };
}

/**
 * The cell's comments, in a popover placed against the cell (GEN-3263): its
 * threads, each with reply / edit / resolve, or — on a cell without any — a
 * box to start one, focused. Escape or a click outside closes it.
 *
 * Driven by `store`, which the grid sets from its context menu, a click on a
 * cell's marker, or a jump; only this re-renders when it opens.
 */
export function CellCommentPopover({
  store,
  changeSetId,
  group,
  threadsByCell,
  onClosed,
}: {
  store: ValueStore<OpenCommentCell | null>;
  changeSetId: string;
  group: As400ExportGroup;
  threadsByCell: CellThreadIndex;
  /** Runs once it closes — the grid takes the keyboard back. */
  onClosed: () => void;
}) {
  const cell = useSyncExternalStore(store.subscribe, store.get);
  return (
    <Popover
      open={cell !== null}
      onOpenChange={(open) => {
        if (open) return;
        store.set(null);
        onClosed();
      }}
    >
      {cell ? (
        <PopoverContent
          anchor={{
            getBoundingClientRect: () =>
              new DOMRect(
                cell.rect.left,
                cell.rect.top,
                cell.rect.width,
                cell.rect.height,
              ),
          }}
          side="bottom"
          align="start"
          className="flex max-h-[min(28rem,70vh)] w-96 flex-col gap-2 overflow-y-auto p-3"
        >
          <CellComments
            cell={cell}
            changeSetId={changeSetId}
            group={group}
            threadsByCell={threadsByCell}
          />
        </PopoverContent>
      ) : undefined}
    </Popover>
  );
}

function CellComments({
  cell,
  changeSetId,
  group,
  threadsByCell,
}: {
  cell: OpenCommentCell;
  changeSetId: string;
  group: As400ExportGroup;
  threadsByCell: CellThreadIndex;
}) {
  const threads = threadsForCell(
    threadsByCell,
    group,
    cell.rowKey,
    cell.columnKey,
  );
  return (
    <>
      <CellLabel cell={cell} group={group} />
      {threads.map((thread) => (
        <ReviewCommentThread
          key={thread.id}
          changeSetId={changeSetId}
          thread={thread}
          className="border-t pt-2 first-of-type:border-t-0 first-of-type:pt-0"
        />
      ))}
      {threads.length === 0 && (
        <NewThreadComposer
          cell={cell}
          changeSetId={changeSetId}
          group={group}
        />
      )}
    </>
  );
}

/**
 * Which cell it is: the row's item number and CDB, when it has them, and the
 * column's header.
 */
function CellLabel({
  cell,
  group,
}: {
  cell: OpenCommentCell;
  group: As400ExportGroup;
}) {
  const label = [
    cell.itemNumber,
    cell.cdb,
    worksheetColumnHeader(group, cell.columnKey),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <p
      className="truncate text-xs font-medium text-muted-foreground"
      title={label}
    >
      <T context="heading of a review grid cell's comments, followed by the row's item number, CDB and the column's header">
        Comments on <Var>{label}</Var>
      </T>
    </p>
  );
}

/** Starts the cell's first thread. */
function NewThreadComposer({
  cell,
  changeSetId,
  group,
}: {
  cell: OpenCommentCell;
  changeSetId: string;
  group: As400ExportGroup;
}) {
  const gt = useGT();
  const create = useCreateChangeSetCommentThread(changeSetId);
  const mentionable = useChangeSetMentionableUsers(changeSetId);
  const row = changeSetCommentRowFromGridKey(cell.rowKey);

  async function handleSubmit(body: string): Promise<boolean> {
    if (!row) return false;
    try {
      await create.mutateAsync({
        changeSetId,
        anchor: {
          group,
          row,
          columnKey: cell.columnKey,
          itemNumber: cell.itemNumber,
          cdb: cell.cdb,
        },
        body,
      });
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Failed to leave the comment', {
              context: 'toast after starting a comment on a grid cell failed',
            }),
      );
      return false;
    }
  }

  return (
    <NoteComposer
      onSubmit={handleSubmit}
      isSubmitting={create.isPending}
      mentionableUsers={mentionable.data}
      // The popover scrolls and clips: a list floating above would be cut off.
      mentionListPlacement="inline"
      disabled={!row}
      // The @ hint waits for the people it would offer.
      placeholder={
        mentionable.data
          ? gt('Leave a comment… Type @ to mention someone', {
              context:
                'placeholder for the first comment on a review grid cell',
            })
          : gt('Leave a comment…', {
              context:
                'placeholder for the first comment on a review grid cell',
            })
      }
    />
  );
}
